import { Action, createAction, handleActions } from "redux-actions";
import { GetReporteSiniestrosRequest } from "~api/types";
import { calculateDate } from "~libs/dateCalculator";
import { queueMessage } from "~shared/snackbarsProvider/actions";
import {
  Dispatch,
  FechaFiltro,
  FiltroValue,
  GetState,
  ServicesReportes,
  TakeValue,
} from "../../types";

/**
 * `estado` se guarda como lista porque el backend espera varios estados a la
 * vez; el selector manda uno solo, que puede ser la combinación "todos".
 */
interface FiltrosSolicitudesState {
  take: TakeValue;
  page: number;
  fechaDesde: FechaFiltro;
  fechaHasta: FechaFiltro;
  searchCriteria: string;
  estado: FiltroValue[];
  negocio: FiltroValue;
  oficina: FiltroValue;
}

const setInitialStateSolicitudes = createAction(
  "SET_INITIAL_STATE_FILTRO_SOLICITUDES"
);

const descargaInited = createAction(
  "GET_REPORTE_SOLICITUDESEMISION_INITED",
  (data: GetReporteSiniestrosRequest) => ({ data })
);
const descargaCompleted = createAction(
  "GET_REPORTE_SOLICITUDESEMISION_COMPLETED"
);
const descargaFailed = createAction(
  "GET_REPORTE_SOLICITUDESEMISION_FAILED",
  (error: unknown) => ({ error })
);

const clearData = createAction(
  "CLEAR_DATA_FILTRO_SOLICITUDESEMISION",
  () => ({})
);

const setPage = createAction(
  "SET_LISTADO_SOLICITUDESEMISION_PAGE",
  (page: number) => ({
    page,
  })
);

const setTake = createAction(
  "SET_LISTADO_SOLICITUDESEMISION_ROWS_PER_PAGE",
  (take: TakeValue) => ({ take })
);

const selectFechaDesde = createAction(
  "SELECT_FECHA_DESDE_FILTRO",
  (fechaDesde: FechaFiltro) => ({ fechaDesde })
);

const selectFechaHasta = createAction(
  "SELECT_FECHA_HASTA_FILTRO",
  (fechaHasta: FechaFiltro) => ({ fechaHasta })
);

const selectTipo = createAction(
  "SELECT_TIPO_SOLICITUDESEMISION",
  (tipo: FiltroValue) => ({
    tipo,
  })
);

const selectSearchCriteria = createAction(
  "SELECT_SEARCH_CRITERIA_FILTRO",
  (searchCriteria: string) => ({
    searchCriteria,
  })
);

const selectOficina = createAction(
  "SELECT_OFICINA_FILTRO",
  (oficina: FiltroValue) => ({
    oficina,
  })
);

/**
 * `isAdminUser` decide qué estados puede pedir el usuario. Al restaurar el
 * filtro desde la URL no se conoce, y el reducer lo trata como no admin.
 */
const selectEstado = createAction(
  "SELECT_ESTADO_FILTRO",
  (estado: FiltroValue, isAdminUser: boolean | undefined) => ({
    estado,
    isAdminUser,
  })
);

const selectNegocio = createAction(
  "SELECT_NEGOCIO_FILTRO",
  (negocio: FiltroValue) => ({
    negocio,
  })
);

const filtrosSolicitudesInitialState: FiltrosSolicitudesState = {
  take: 10,
  page: 0,
  fechaDesde: calculateDate(new Date(), -30),
  fechaHasta: new Date(),
  searchCriteria: "",
  estado: ["Pendiente,PendienteCompania,DatosIncompletos"],
  negocio: "",
  oficina: "",
};

const solicitudesEmisionFiltrosReducer = handleActions<
  FiltrosSolicitudesState,
  any
>(
  {
    [setInitialStateSolicitudes.toString()]: (): FiltrosSolicitudesState => {
      return {
        ...filtrosSolicitudesInitialState,
      };
    },
    [setPage.toString()]: (
      state: FiltrosSolicitudesState,
      action: Action<{ page: number }>
    ): FiltrosSolicitudesState => {
      return {
        ...state,
        page: action.payload.page,
      };
    },
    [setTake.toString()]: (
      state: FiltrosSolicitudesState,
      action: Action<{ take: TakeValue }>
    ): FiltrosSolicitudesState => {
      return {
        ...state,
        take: action.payload.take,
      };
    },
    [selectFechaDesde.toString()]: (
      state: FiltrosSolicitudesState,
      action: Action<{ fechaDesde: FechaFiltro }>
    ): FiltrosSolicitudesState => {
      return {
        ...state,
        fechaDesde: action.payload.fechaDesde,
      };
    },
    [selectFechaHasta.toString()]: (
      state: FiltrosSolicitudesState,
      action: Action<{ fechaHasta: FechaFiltro }>
    ): FiltrosSolicitudesState => {
      return {
        ...state,
        fechaHasta: action.payload.fechaHasta,
      };
    },
    [selectSearchCriteria.toString()]: (
      state: FiltrosSolicitudesState,
      action: Action<{ searchCriteria: string }>
    ): FiltrosSolicitudesState => {
      return {
        ...state,
        searchCriteria: action.payload.searchCriteria,
      };
    },
    [selectOficina.toString()]: (
      state: FiltrosSolicitudesState,
      action: Action<{ oficina: FiltroValue }>
    ): FiltrosSolicitudesState => {
      return {
        ...state,
        oficina: action.payload.oficina,
      };
    },
    [selectEstado.toString()]: (
      state: FiltrosSolicitudesState,
      action: Action<{ estado: FiltroValue; isAdminUser?: boolean }>
    ): FiltrosSolicitudesState => {
      let hasAccessToDatosIncompletos =
        action.payload.isAdminUser &&
        action.payload.estado === "DatosIncompletos";

      let hasAccessToAllAdminStates =
        action.payload.isAdminUser &&
        action.payload.estado ===
          "Pendiente,PendienteCompania,DatosIncompletos";

      let hasAccessToAllNonAdminStates =
        !action.payload.isAdminUser &&
        action.payload.estado === "Pendiente,PendienteCompania";

      let estado =
        action.payload.estado !== "Pendiente" &&
        action.payload.estado !== "PendienteCompania" &&
        action.payload.estado !== "DatosIncompletos" &&
        !hasAccessToDatosIncompletos &&
        !hasAccessToAllAdminStates &&
        !hasAccessToAllNonAdminStates
          ? "Pendiente,PendienteCompania,DatosIncompletos"
          : action.payload.estado;
      return {
        ...state,
        estado: [estado],
      };
    },
    [selectNegocio.toString()]: (
      state: FiltrosSolicitudesState,
      action: Action<{ negocio: FiltroValue }>
    ): FiltrosSolicitudesState => {
      return {
        ...state,
        negocio: action.payload.negocio,
      };
    },
  },
  filtrosSolicitudesInitialState
);

const descargarReporte = (data: GetReporteSiniestrosRequest) => {
  return async (
    dispatch: Dispatch,
    getState: GetState,
    services: ServicesReportes
  ) => {
    try {
      dispatch(descargaInited(data));
      let response = await services.api.reportes().getSiniestros(data);

      // create a fake anchor and click it programatically to
      // force the file to download
      var xhttp = new XMLHttpRequest();
      xhttp.onreadystatechange = function (this: XMLHttpRequest) {
        if (this.readyState === 4 && this.status === 200) {
          var downloadUrl = URL.createObjectURL(xhttp.response);
          var a = document.createElement("a");
          document.body.appendChild(a);
          a.style.display = "none";
          a.href = downloadUrl;
          a.download = "";
          a.click();
        }
      };

      xhttp.open("GET", response.url, true);
      xhttp.responseType = "blob";
      xhttp.send();

      dispatch(descargaCompleted());
    } catch (error) {
      console.error(error);
      dispatch(descargaFailed(error));
      dispatch(
        queueMessage(
          "Ocurrió un error al descargar el reporte. Intente nuevamente."
        )
      );
    }
  };
};

export { solicitudesEmisionFiltrosReducer };
export {
  setPage,
  setTake,
  selectFechaDesde,
  selectFechaHasta,
  selectSearchCriteria,
  setInitialStateSolicitudes,
  selectEstado,
  selectNegocio,
  selectOficina,
  selectTipo,
  descargarReporte,
  clearData,
};
